import React, { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Button } from 'primereact/button';
import Grid from '../../../components/components/Grid/Grid';
import CarrierManagementAPI from '../Services/CarrierManagementAPI';
import AgencyMasterCodesService from './AgencyMasterCodesService';
import LookupService from '../../../Services/LookupService';
import { SelectOption } from '../../../shared/components/input';
import { useConfirmationModalContext } from '../../../shared/components/ModalConfirmationContext';
import { Spinner } from '../../../components';
import { RootState } from '../../../shared/store';
import { updateLookupData } from '../../../customer/customer-slice/Customer-lookup-slice';
import * as constant from '../../../shared/util/constant';
import * as label from '../../../shared/util/label';
import * as tblConsts from '../../../shared/util/tableConstant';
import './AgencyMasterCodesGrid.scss';

interface AgencyMasterCodesGridProps {
    carrierId: string;
}

interface IGridState {
    agentCodes: any[];
    gridColumns: any[];
    isRowDataAddedOrEdited: boolean;
}

const initState: IGridState = {
    agentCodes: [],
    gridColumns: [],
    isRowDataAddedOrEdited: false,
};

const AgencyMasterCodesGrid: React.FC<AgencyMasterCodesGridProps> = ({
    carrierId,
}) => {
    const dispatch = useDispatch();
    const modalContext = useConfirmationModalContext();

    const serviceRef = useRef(new AgencyMasterCodesService());
    const apiRef = useRef(new CarrierManagementAPI());

    const reduxAgencyList = useSelector((state: RootState) => state?.LookupState.agencyList);
    const reduxLOBList = useSelector((state: RootState) => state?.LookupState.lineOfBusinessList);
    const reduxStateUSList = useSelector((state: RootState) => state?.LookupState.stateUSList);

    const [gridState, setGridState] = useState<IGridState>(initState);
    const [navigateToFirstPage, setNavigateToFirstPage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lookupsLoaded, setLookupsLoaded] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    const { agentCodes, gridColumns } = gridState;

    useEffect(() => {
        setHasFetched(false);
        setGridState(initState);
    }, [carrierId]);

    useEffect(() => {
        const fetchLookups = async () => {
            try {
                const service = serviceRef.current;

                const [agencyData, lobData, usStateData] = await Promise.all([
                    reduxAgencyList.length > 0 ? Promise.resolve(null) : LookupService.fetchAgencies(),
                    reduxLOBList.length > 0 ? Promise.resolve(null) : LookupService.fetchLineofBusiness(),
                    reduxStateUSList.length > 0 ? Promise.resolve(null) : LookupService.fetchUSStates(),
                ]);

                const [marketTypeData] = await Promise.all([
                    LookupService.getLookupData(tblConsts.MARKET_TYPE_TABLE_ID),
                ]);

                // Push fresh data to Redux for other components
                if (agencyData) {
                    const opts = agencyData.map((a: any) => ({ label: a.lookupValue, value: a.id }));
                    dispatch(updateLookupData({ data: opts, type: constant.SlicerLookUpConstant.AGENCY }));
                }
                if (lobData) {
                    const opts = lobData.map((l: any) => ({ label: l.lookupValue, value: l.id }));
                    dispatch(updateLookupData({ data: opts, type: constant.SlicerLookUpConstant.Line_Of_Business }));
                }
                if (usStateData) {
                    const opts = usStateData.map((s: any) => ({ label: s.lookupCode, value: s.lookupCode }));
                    dispatch(updateLookupData({ data: opts, type: constant.SlicerLookUpConstant.US_STATE }));
                }

                const finalAgencyOpts: SelectOption[] = agencyData
                    ? agencyData.map((a: any) => ({ label: a.lookupValue, value: a.id }))
                    : [...reduxAgencyList];
                const finalLOBOpts: SelectOption[] = lobData
                    ? lobData.map((l: any) => ({ label: l.lookupValue, value: l.id }))
                    : [...reduxLOBList];
                const finalStateOpts: SelectOption[] = usStateData
                    ? usStateData.map((s: any) => ({ label: s.lookupCode, value: s.lookupCode }))
                    : [...reduxStateUSList];
                const marketTypeOpts: SelectOption[] = marketTypeData.map((m: any) => ({
                    label: m.lookupValue,
                    value: m.id,
                }));

                const cols = service.getGridColumns(
                    onEditingCellDataChange,
                    onOriginalRowDataChange,
                    gridCellClickHandler,
                    finalAgencyOpts,
                    finalLOBOpts,
                    marketTypeOpts,
                    finalStateOpts
                );
                setGridState(prev => ({ ...prev, gridColumns: cols }));
                setLookupsLoaded(true);
            } catch {
                toast.error(constant.ERROR_MESSAGE_LOAD);
            }
        };
        fetchLookups();
    }, []);

    // Fetch data when ready
    useEffect(() => {
        if (!carrierId || !lookupsLoaded || hasFetched) return;
        fetchAgentCodes();
    }, [carrierId, lookupsLoaded, hasFetched]);

    const fetchAgentCodes = async () => {
        if (!carrierId) return;
        setLoading(true);
        try {
            const api = apiRef.current;
            const service = serviceRef.current;
            const data = await api.getAgentCodes(carrierId);
            const enriched = data.map((row: any) => {
                const r = { ...row };
                service.enrichRow(r);
                return r;
            });
            setGridState(prev => ({ ...prev, agentCodes: enriched, isRowDataAddedOrEdited: false }));
            setHasFetched(true);
        } catch {
            toast.error(constant.ERROR_MESSAGE_LOAD);
        }
        setLoading(false);
    };

    const onEditingCellDataChange = (editingRow: any, column: any, value: any) => {
        editingRow[column.fieldName] = value;
        setGridState(prev => ({ ...prev, agentCodes: [...prev.agentCodes] }));
    };

    const onOriginalRowDataChange = (originalRow: any, fieldName: string, value: any) => {
        originalRow[fieldName] = value;
        setGridState(prev => ({ ...prev, agentCodes: [...prev.agentCodes] }));
    };

    const gridCellClickHandler = (originalRow: any, _column: any, additionalData: any) => {
        switch (additionalData) {
            case 'edit_row':   editRowClickHandler(originalRow); break;
            case 'save_row':   saveRowClickHandler(originalRow); break;
            case 'cancel_row_editing': cancelRowClickHandler(originalRow); break;
            case 'delete_row': deleteRowClickHandler(originalRow); break;
            default: break;
        }
    };

    const editRowClickHandler = (originalRow: any) => {
        const editingRow = { ...originalRow };
        if (editingRow.effDate && typeof editingRow.effDate === 'string') {
            editingRow.effDate = new Date(editingRow.effDate);
        }
        originalRow.editingRow = editingRow;
        originalRow.enableGridRowEditing = true;

        setGridState(prev => {
            prev.agentCodes.forEach(r => {
                if (r !== originalRow) r.isGridInEditMode = true;
            });
            return { ...prev, agentCodes: [...prev.agentCodes], isRowDataAddedOrEdited: true };
        });
    };

    const saveRowClickHandler = async (originalRow: any) => {
        const editingRow = originalRow.editingRow;
        if (!editingRow) return;

        const validationErrors = serviceRef.current.validateRow(originalRow);
        editingRow.errors = validationErrors;
        if (Object.keys(validationErrors).length > 0) {
            setGridState(prev => ({ ...prev, agentCodes: [...prev.agentCodes] }));
            return;
        }

        try {
            const api = apiRef.current;
            const service = serviceRef.current;
            const isNew = !editingRow.id || editingRow.id === constant.DEFAULT_GUID;

            const dto = {
                id: isNew ? constant.DEFAULT_GUID : editingRow.id,
                carrierId,
                agencyId: editingRow.agencyId || null,
                lobId: editingRow.lobId || null,
                agentCode: editingRow.agentCode || '',
                marketTypeId: editingRow.marketTypeId || null,
                effDate: editingRow.effDate instanceof Date
                    ? editingRow.effDate
                    : (editingRow.effDate ? new Date(editingRow.effDate) : null),
                propertyState: editingRow.propertyState || null,
                note: editingRow.note || null,
            };

            const saved = isNew
                ? await api.createAgentCode(carrierId, dto)
                : await api.updateAgentCode(carrierId, editingRow.id, dto);

            service.enrichRow(saved);
            toast.success(isNew ? constant.CarrierManagementConstants.AGENCY_MASTER_CODES.CREATED : constant.CarrierManagementConstants.AGENCY_MASTER_CODES.UPDATED);
            await fetchAgentCodes();
        } catch (ex: any) {
            const msg = ex?.data?.message || ex?.message || constant.CarrierManagementConstants.AGENCY_MASTER_CODES.SAVE_ERROR;
            toast.error(msg);
        }
    };

const cancelRowClickHandler = async (originalRow: any) => {
        const confirmed = await modalContext.showConfirmation(constant.CONFIRM_EXIT);
        if (!confirmed) return;

        setGridState(prev => {
            prev.agentCodes.forEach(r => {
                r.isGridInEditMode = false;
                r.enableGridRowEditing = false;
                r.editingRow = undefined;
            });
            const isNew = !originalRow.id || originalRow.id === constant.DEFAULT_GUID;
            const rows = isNew
                ? prev.agentCodes.filter(r => r !== originalRow)
                : [...prev.agentCodes];
            return { ...prev, agentCodes: rows, isRowDataAddedOrEdited: false };
        });
    };

    const deleteRowClickHandler = async (originalRow: any) => {
        const confirmed = await modalContext.showConfirmation(constant.CarrierManagementConstants.AGENCY_MASTER_CODES.DELETE_CONFIRM);
        if (!confirmed) return;

        try {
            await apiRef.current.deleteAgentCode(carrierId, originalRow.id);
            toast.success(constant.CarrierManagementConstants.AGENCY_MASTER_CODES.DELETED);
            await fetchAgentCodes();
        } catch (ex: any) {
            const msg = ex?.data?.message || ex?.message || constant.CarrierManagementConstants.AGENCY_MASTER_CODES.DELETE_ERROR;
            toast.error(msg);
        }
    };

    const onAddRowClick = () => {
        if (!carrierId) return;
        setGridState(prev => {
            prev.agentCodes.forEach(r => (r.isGridInEditMode = true));
            const newRow = serviceRef.current.getNewRow(carrierId);
            return {
                ...prev,
                agentCodes: [newRow, ...prev.agentCodes],
                isRowDataAddedOrEdited: true,
            };
        });
        setNavigateToFirstPage(true);
    };

    return (
        <div className="agent-code-grid-container">
            <div className="d-flex justify-content-end mb-2">
                <Button
                    label={label.CarrierManagementLabels.ADD_ROW_LABEL}
                    icon="bi bi-plus-circle"
                    onClick={onAddRowClick}
                    disabled={gridState.isRowDataAddedOrEdited}
                    className="p-button-sm"
                />
            </div>
            {loading && <Spinner loading={true} type="SyncLoader" />}
            {!loading && gridColumns.length > 0 && (
                <div className="agent-code-grid-div">
                    <Grid
                        data={agentCodes}
                        showFilters={true}
                        id="agencyMasterCodesGrid"
                        retainGridState={true}
                        columns={gridColumns}
                        hiddenColumns={['selection']}
                        sortDefault={[]}
                        paginationDisabled={true}
                        grdTabelClass="table-striped table-sm table-bordered"
                        grdTableContainerClass="agent-code-table-container"
                        navigateToFirstPage={navigateToFirstPage}
                        onNavigateToFirstPage={() => setNavigateToFirstPage(false)}
                    />
                </div>
            )}
            {!loading && agentCodes.length === 0 && hasFetched && (
                <div className="text-center text-muted py-3">{label.AgencyMasterCodesLabels.NO_RECORDS_FOUND}</div>
            )}
        </div>
    );
};

export default AgencyMasterCodesGrid;
